import React, { useMemo, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import VerifiedIcon from '@material-ui/icons/CheckCircle';
import { tokens } from '../../theme';
import { formatDate } from '../Common/useEntries';
import { ExpandableText } from './ReadMore';
import CommentForm from './CommentForm';

const PAGE_SIZE = 10;
const AVATAR_COLOURS = ['#225491', '#1f7a55', '#8a5a00', '#6b3fa0', '#a62121', '#0f6e7a', '#4b5563'];

const focusRing = { outline: `2px solid ${tokens.blue500}`, outlineOffset: 2, borderRadius: 2 };

const useStyles = makeStyles({
    toolbar: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 8,
        padding: '12px 0',
        borderBottom: `1px solid ${tokens.border}`,
        marginBottom: 20,
        color: tokens.muted,
        fontSize: '0.9375rem',
    },
    sortButton: {
        padding: '4px 10px',
        border: 0,
        borderRadius: 6,
        background: 'none',
        font: 'inherit',
        fontSize: '0.875rem',
        fontWeight: 600,
        color: tokens.muted,
        cursor: 'pointer',
        '&:hover': { background: tokens.surface },
        '&[aria-pressed="true"]': { background: tokens.blue50, color: tokens.blue700 },
        '&:focus-visible': focusRing,
    },
    list: { listStyle: 'none', margin: 0, padding: 0 },
    thread: { marginBottom: 20 },
    row: { display: 'flex', gap: 8, alignItems: 'flex-start' },
    avatar: {
        flex: '0 0 auto',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: '50%',
        color: tokens.white,
        fontWeight: 700,
        userSelect: 'none',
    },
    body: { minWidth: 0, flex: '1 1 auto' },
    bubble: {
        display: 'inline-block',
        maxWidth: '100%',
        padding: '8px 14px 10px',
        borderRadius: 18,
        background: '#eef1f5',
        overflowWrap: 'anywhere',
    },
    officialBubble: { background: tokens.blue50, border: `1px solid ${tokens.blue100}` },
    name: {
        display: 'inline-flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 4,
        fontWeight: 700,
        fontSize: '0.875rem',
        color: tokens.ink,
    },
    badge: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 3,
        marginLeft: 4,
        fontSize: '0.75rem',
        fontWeight: 600,
        color: tokens.blue600,
    },
    text: { margin: '2px 0 0', whiteSpace: 'pre-line', fontSize: '0.9375rem', lineHeight: 1.5, color: tokens.ink },
    meta: {
        display: 'flex',
        gap: 12,
        alignItems: 'center',
        margin: '4px 0 0 14px',
        fontSize: '0.75rem',
        color: tokens.muted,
    },
    link: {
        padding: 0,
        border: 0,
        background: 'none',
        font: 'inherit',
        fontWeight: 700,
        color: tokens.muted,
        cursor: 'pointer',
        '&:hover': { textDecoration: 'underline' },
        '&:focus-visible': focusRing,
    },
    replies: { listStyle: 'none', margin: '12px 0 0', padding: '0 0 0 48px' },
    reply: { marginBottom: 12 },
    replyForm: {
        margin: '12px 0 0 48px',
        padding: 16,
        border: `1px solid ${tokens.border}`,
        borderRadius: tokens.radius,
        background: tokens.surface,
    },
    more: {
        display: 'block',
        width: '100%',
        padding: '10px 0',
        border: `1px solid ${tokens.border}`,
        borderRadius: tokens.radius,
        background: tokens.white,
        font: 'inherit',
        fontWeight: 600,
        color: tokens.blue600,
        cursor: 'pointer',
        '&:hover': { background: tokens.surface },
        '&:focus-visible': focusRing,
    },
});

function initials(name) {
    const parts = name.trim().split(/\s+/).filter(Boolean);
    if (!parts.length) return '?';
    return (parts[0][0] + (parts.length > 1 ? parts[parts.length - 1][0] : '')).toUpperCase();
}

function colourFor(name) {
    let hash = 0;
    for (let i = 0; i < name.length; i += 1) hash = (hash * 31 + name.charCodeAt(i)) | 0;
    return AVATAR_COLOURS[Math.abs(hash) % AVATAR_COLOURS.length];
}

/** "Just now", "5 min ago", "3 h ago", "Yesterday", "4 days ago", then the date. */
export function timeAgo(value, now = Date.now()) {
    const time = new Date(value).getTime();
    if (Number.isNaN(time)) return '';
    const minutes = Math.floor((now - time) / 60000);
    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes} min ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours} h ago`;
    const days = Math.floor(hours / 24);
    if (days < 7) return days === 1 ? 'Yesterday' : `${days} days ago`;
    return formatDate(value);
}

function Avatar({ name, official, size }) {
    const classes = useStyles();
    return (
        <span
            aria-hidden="true"
            className={classes.avatar}
            style={{
                width: size,
                height: size,
                fontSize: size * 0.36,
                background: official ? tokens.blue900 : colourFor(name),
                boxShadow: official ? `0 0 0 2px ${tokens.gold}` : undefined,
            }}
        >
            {official ? 'MoF' : initials(name)}
        </span>
    );
}

function Comment({ comment, onReply, small }) {
    const classes = useStyles();
    const bubbleClass = comment.isMinistryReply ? `${classes.bubble} ${classes.officialBubble}` : classes.bubble;
    return (
        <article className={classes.row} aria-label={`Comment by ${comment.authorName}`}>
            <Avatar name={comment.authorName} official={comment.isMinistryReply} size={small ? 32 : 40} />
            <div className={classes.body}>
                <div className={bubbleClass}>
                    <span className={classes.name}>
                        {comment.authorName}
                        {comment.isMinistryReply && (
                            <span className={classes.badge}>
                                <VerifiedIcon style={{ fontSize: 14 }} />
                                Official reply
                            </span>
                        )}
                    </span>
                    {/* Visitor text: rendered as plain text, never as HTML. */}
                    <ExpandableText text={comment.content} className={classes.text} />
                </div>
                <div className={classes.meta}>
                    {comment.publishedAt && (
                        <time dateTime={comment.publishedAt} title={new Date(comment.publishedAt).toLocaleString('en-GB')}>
                            {timeAgo(comment.publishedAt)}
                        </time>
                    )}
                    {onReply && (
                        <button type="button" className={classes.link} onClick={onReply}>
                            Reply
                        </button>
                    )}
                </div>
            </div>
        </article>
    );
}

/**
 * Comments grouped into threads, social-media style: each top-level comment with its replies
 * indented beneath it. Replies whose parent has been unpublished are not shown.
 */
export default function CommentThread({ comments, postId, onChanged }) {
    const classes = useStyles();
    const [newestFirst, setNewestFirst] = useState(true);
    const [visible, setVisible] = useState(PAGE_SIZE);
    const [replyingTo, setReplyingTo] = useState(null);

    const threads = useMemo(() => {
        const repliesByParent = new Map();
        comments.filter((c) => c.parentId).forEach((reply) => {
            repliesByParent.set(reply.parentId, [...(repliesByParent.get(reply.parentId) || []), reply]);
        });
        const byDate = (a, b) => a.publishedAt.localeCompare(b.publishedAt);
        const roots = comments
            .filter((c) => !c.parentId)
            .map((root) => ({ ...root, replies: (repliesByParent.get(root.id) || []).sort(byDate) }))
            .sort(byDate);
        return newestFirst ? roots.reverse() : roots;
    }, [comments, newestFirst]);

    if (!threads.length) return null;

    const total = threads.reduce((count, thread) => count + 1 + thread.replies.length, 0);
    const shown = threads.slice(0, visible);
    const remaining = threads.length - shown.length;

    return (
        <div>
            <div className={classes.toolbar}>
                <span>{total === 1 ? '1 comment' : `${total} comments`}</span>
                <span role="group" aria-label="Sort comments">
                    <button type="button" className={classes.sortButton} aria-pressed={newestFirst} onClick={() => setNewestFirst(true)}>
                        Newest
                    </button>
                    <button type="button" className={classes.sortButton} aria-pressed={!newestFirst} onClick={() => setNewestFirst(false)}>
                        Oldest
                    </button>
                </span>
            </div>

            <ul className={classes.list}>
                {shown.map((thread) => (
                    <li key={thread.id} className={classes.thread}>
                        <Comment comment={thread} onReply={() => setReplyingTo({ id: thread.id, name: thread.authorName })} />
                        {thread.replies.length > 0 && (
                            <ul className={classes.replies} aria-label={`Replies to ${thread.authorName}`}>
                                {thread.replies.map((reply) => (
                                    <li key={reply.id} className={classes.reply}>
                                        <Comment
                                            comment={reply}
                                            small
                                            onReply={() => setReplyingTo({ id: thread.id, name: reply.authorName })}
                                        />
                                    </li>
                                ))}
                            </ul>
                        )}
                        {replyingTo && replyingTo.id === thread.id && (
                            <div className={classes.replyForm}>
                                <CommentForm
                                    postId={postId}
                                    parentId={thread.id}
                                    replyTo={replyingTo.name}
                                    onCancel={() => setReplyingTo(null)}
                                    onSubmitted={() => {
                                        setReplyingTo(null);
                                        if (onChanged) onChanged();
                                    }}
                                />
                            </div>
                        )}
                    </li>
                ))}
            </ul>

            {remaining > 0 && (
                <button type="button" className={classes.more} onClick={() => setVisible(visible + PAGE_SIZE)}>
                    {`View more comments (${remaining})`}
                </button>
            )}
        </div>
    );
}
